// src/context/I18nContext.js
//
// The frontend half of the translation system (see internal/models/
// language.go and internal/handlers/translation_handler.go on the backend).
// Resolution order for "what language is this session in":
//   1. The authenticated user's own saved preference (models.User.
//      PreferredLanguage, fetched via getUserProfile once a token exists) —
//      EXCEPT when the person just switched languages on the Login screen
//      itself right before signing in: that explicit pick wins instead and
//      gets saved as the new preference (see preLoginChoiceRef below),
//      rather than the account's old saved language silently overwriting it
//      the instant login succeeds.
//   2. Whatever was last chosen on this browser (localStorage) — covers the
//      Login page, before anyone's signed in.
//   3. Whichever Language the backend marked IsDefault (English, seeded by
//      internal/config/seed_languages.go).
//
// Every retrofitted page calls useTranslation(namespace) once; the actual
// English-fallback logic for an untranslated key lives server-side (see
// resolveNamespace in translation_handler.go) — this file's own t(key,
// fallback) second argument is only a LOCAL placeholder shown for the
// instant before that namespace's bundle has finished its first fetch, so
// the page never flashes a raw translation key.
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getPublicLanguages, getPublicTranslations, getUserProfile, updatePreferredLanguage } from "../services/api";
import { AuthContext } from "./AuthContext";

export const I18nContext = createContext();

const STORAGE_KEY = "preferredLanguage";

export function I18nProvider({ children }) {
  const { token } = useContext(AuthContext);
  const [languages, setLanguages] = useState([]);
  const [languageCode, setLanguageCode] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [bundles, setBundles] = useState({});
  const loadingRef = useRef(new Set());
  // Tracks a language picked from the LanguagePicker while still on the
  // Login screen (no token yet) — see switchLanguage and the [token] effect
  // below for why this exists: without it, that pick gets silently
  // overwritten by the account's OLD saved preference the instant login
  // succeeds (reported bug: switch to Arabic on Login, sign in, land back
  // in English).
  const preLoginChoiceRef = useRef(null);

  // Enabled languages list — also supplies the default-language fallback
  // when nothing's been chosen on this browser yet.
  useEffect(() => {
    getPublicLanguages()
      .then((langs) => {
        setLanguages(langs || []);
        setLanguageCode((prev) => {
          if (prev) return prev;
          const def = (langs || []).find((l) => l.is_default);
          return def ? def.code : prev;
        });
      })
      .catch(() => {});
  }, []);

  // Once signed in, the user's own saved choice (if any) wins over
  // whatever this browser had guessed pre-login — UNLESS the person just
  // explicitly picked a language on the Login screen itself (tracked via
  // preLoginChoiceRef by switchLanguage below): that's a deliberate choice
  // made seconds ago, not stale browser state left over from someone else,
  // so it wins instead and gets pushed to the account as the new saved
  // preference rather than getting clobbered by the old one.
  useEffect(() => {
    if (!token) return;
    const preLoginChoice = preLoginChoiceRef.current;
    preLoginChoiceRef.current = null;
    if (preLoginChoice) {
      updatePreferredLanguage(token, preLoginChoice).catch(() => {});
      return; // languageCode/localStorage already reflect this choice
    }
    getUserProfile(token)
      .then((u) => {
        if (u?.preferred_language) {
          setLanguageCode(u.preferred_language);
          localStorage.setItem(STORAGE_KEY, u.preferred_language);
        }
      })
      .catch(() => {});
  }, [token]);

  // Flip <html dir="ltr|rtl" lang="..."> app-wide the moment the resolved
  // language changes — this is what actually mirrors layout for Arabic.
  useEffect(() => {
    const current = languages.find((l) => l.code === languageCode);
    document.documentElement.dir = current?.direction || "ltr";
    document.documentElement.lang = languageCode || "en";
  }, [languageCode, languages]);

  const switchLanguage = useCallback(
    (code) => {
      setLanguageCode(code);
      localStorage.setItem(STORAGE_KEY, code);
      setBundles({}); // every mounted namespace's effect re-fetches under the new language
      if (token) {
        updatePreferredLanguage(token, code).catch(() => {});
      } else {
        // No one's signed in yet (this is the Login screen's own
        // LanguagePicker) — there's no profile to save this to right now,
        // so remember it here and the [token] effect above pushes it to
        // the account the moment login succeeds, instead of letting that
        // effect's own getUserProfile fetch silently overwrite this pick
        // with whatever language was saved on the account before.
        preLoginChoiceRef.current = code;
      }
    },
    [token]
  );

  const loadNamespace = useCallback(
    (namespace, langCode) => {
      const cacheKey = `${namespace}::${langCode}`;
      if (!langCode || bundles[cacheKey] || loadingRef.current.has(cacheKey)) return;
      loadingRef.current.add(cacheKey);
      getPublicTranslations(namespace, langCode)
        .then((data) => setBundles((prev) => ({ ...prev, [cacheKey]: data || {} })))
        .catch(() => setBundles((prev) => ({ ...prev, [cacheKey]: {} })))
        .finally(() => loadingRef.current.delete(cacheKey));
    },
    [bundles]
  );

  // Force one namespace/language pair to re-fetch, bypassing the cache
  // loadNamespace above otherwise respects. Used by
  // NamespaceTranslationsButton right after a page-scoped CSV import
  // succeeds, so the page's own text updates immediately instead of
  // waiting for a full reload/language switch to clear `bundles`.
  const reloadNamespace = useCallback((namespace, langCode) => {
    if (!langCode) return;
    const cacheKey = `${namespace}::${langCode}`;
    loadingRef.current.delete(cacheKey);
    getPublicTranslations(namespace, langCode)
      .then((data) => setBundles((prev) => ({ ...prev, [cacheKey]: data || {} })))
      .catch(() => {});
  }, []);

  return (
    <I18nContext.Provider value={{ languages, languageCode, switchLanguage, bundles, loadNamespace, reloadNamespace }}>
      {children}
    </I18nContext.Provider>
  );
}

// useTranslation(namespace) — call once per page/component that has
// translatable strings. t(key, fallback) returns the resolved text once
// loaded; before that (or if this specific key is somehow still missing)
// it returns `fallback` if given, else the raw key.
export function useTranslation(namespace) {
  const { languageCode, bundles, loadNamespace } = useContext(I18nContext);
  const cacheKey = `${namespace}::${languageCode}`;

  useEffect(() => {
    loadNamespace(namespace, languageCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, languageCode]);

  const bundle = bundles[cacheKey];
  const t = useCallback((key, fallback) => (bundle && key in bundle ? bundle[key] : fallback ?? key), [bundle]);
  return { t, ready: !!bundle };
}
